import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { searchProducts } from '@/api/client';

interface ProductOption {
  product_id: string;
  name: string;
  name_he?: string;
  category?: string;
  price?: number;
  unit?: string;
  suggested_quantity?: number;
}

interface SelectedItem {
  product_id: string;
  name: string;
  quantity: number;
  unit: string;
  price?: number;
}

interface ProductSelectorCardProps {
  data: {
    suggestions: ProductOption[];
    categories?: string[];
    context?: string;
    allow_search?: boolean;
  };
  onSelect: (selection: Record<string, unknown>) => void;
}

export function ProductSelectorCard({ data, onSelect }: ProductSelectorCardProps) {
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProductOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Debounced live search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await searchProducts(searchQuery);
        const results = (res.data?.results || res.data || []).map((p: Record<string, unknown>) => ({
          product_id: String(p.product_id || p.id || ''),
          name: String(p.name || ''),
          name_he: p.name_he as string,
          category: p.category as string,
          price: p.price as number,
          unit: (p.unit as string) || 'kg',
        }));
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const addItem = (product: ProductOption) => {
    const existing = selectedItems.find((i) => i.product_id === product.product_id);
    if (existing) return;
    setSelectedItems((prev) => [
      ...prev,
      {
        product_id: product.product_id,
        name: product.name,
        quantity: product.suggested_quantity || 1,
        unit: product.unit || 'kg',
        price: product.price,
      },
    ]);
    searchInputRef.current?.focus();
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      setSelectedItems((prev) => prev.filter((i) => i.product_id !== productId));
      return;
    }
    setSelectedItems((prev) =>
      prev.map((i) => (i.product_id === productId ? { ...i, quantity } : i)),
    );
  };

  const handleConfirm = () => {
    onSelect({ items: selectedItems });
  };

  const showSearchResults = searchQuery.trim().length > 0;
  const suggestions = data.suggestions || [];
  const filteredSuggestions = activeCategory
    ? suggestions.filter((p) => p.category === activeCategory)
    : suggestions;
  const displayProducts = showSearchResults ? searchResults : filteredSuggestions;
  const resultLabel = showSearchResults
    ? t('agentChat.cards.searchResults', 'Search results')
    : t('agentChat.cards.suggestedProducts', 'Suggested products');

  const total = selectedItems.reduce((sum, i) => sum + (i.price || 0) * i.quantity, 0);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden my-2">
      {data.context && (
        <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-500">{data.context}</div>
      )}
      <div className="p-3">
        <p className="text-sm font-medium text-gray-700 mb-2">
          {t('agentChat.cards.selectProducts', 'Select products:')}
        </p>

        {data.allow_search !== false && (
          <div className="mb-3 rounded-xl border border-primary-100 bg-primary-50/50 p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-xs font-semibold text-primary-700">
                {t('agentChat.cards.searchAndAdd', 'Search and add items')}
              </p>
              {selectedItems.length > 0 && (
                <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-primary-700 border border-primary-200">
                  {t('agentChat.cards.selectedCount', '{{count}} selected', { count: selectedItems.length })}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mb-2">
              {t('agentChat.cards.searchProductsHelp', 'Fuzzy search works with partial names and small typos.')}
            </p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && displayProducts.length > 0) {
                    e.preventDefault();
                    addItem(displayProducts[0]);
                  }
                }}
                placeholder={t('agentChat.cards.searchProducts', 'Search products...')}
                className="w-full pl-8 pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                dir="auto"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <span className="inline-block w-3 h-3 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Category tabs */}
        {!showSearchResults && data.categories && data.categories.length > 0 && (
          <div className="flex gap-1 mb-2 overflow-x-auto pb-1">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
                !activeCategory ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {t('agentChat.cards.allCategories', 'All')}
            </button>
            {data.categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
                  activeCategory === cat ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Best guesses label for suggestions */}
        {!showSearchResults && suggestions.length > 0 && (
          <p className="text-xs font-medium text-gray-500 mb-1.5">
            ⭐ {t('agentChat.cards.bestGuesses', 'Best guesses')}
          </p>
        )}

        {/* Product list */}
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-xs font-medium text-gray-500">{resultLabel}</p>
          <span className="text-[11px] text-gray-400">{displayProducts.length}</span>
        </div>
        <div className="space-y-1 max-h-48 overflow-y-auto mb-3">
          {showSearchResults && searchResults.length === 0 && !isSearching && (
            <div className="py-3 text-center text-xs text-gray-400">
              {t('common.noResults', 'No results found')}
            </div>
          )}
          {displayProducts.map((p) => {
            const isSelected = selectedItems.some((i) => i.product_id === p.product_id);
            return (
              <button
                key={p.product_id}
                onClick={() => addItem(p)}
                disabled={isSelected}
                className={`w-full flex items-center justify-between p-2 rounded-lg border text-left transition-colors ${
                  isSelected
                    ? 'border-primary-300 bg-primary-50 opacity-60'
                    : showSearchResults
                      ? 'border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                      : 'border-primary-200 bg-primary-50/40 hover:border-primary-400 hover:bg-primary-50'
                }`}
              >
                <div>
                  <span className="text-sm font-medium">{p.name}</span>
                  {p.name_he && <span className="text-sm text-gray-500 ml-1">/ {p.name_he}</span>}
                  {p.category && <span className="text-xs text-gray-400 ml-2">[{p.category}]</span>}
                </div>
                {p.price != null && (
                  <span className="text-xs text-gray-500">{p.price}₪/{p.unit || 'kg'}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected items with quantity controls */}
        {selectedItems.length > 0 && (
          <div className="border-t pt-2">
            <p className="text-xs font-medium text-gray-500 mb-1.5">
              {t('agentChat.cards.selectedItems', 'Selected items:')}
            </p>
            <div className="space-y-1.5 mb-2">
              {selectedItems.map((item) => (
                <div key={item.product_id} className="flex items-center justify-between bg-gray-50 rounded-lg px-2 py-1.5">
                  <span className="text-sm text-gray-700 flex-1">{item.name}</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                      className="w-6 h-6 flex items-center justify-center rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-xs"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(item.product_id, Number(e.target.value))}
                      className="w-14 text-center text-sm border rounded px-1 py-0.5"
                      min={0}
                    />
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                      className="w-6 h-6 flex items-center justify-center rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-xs"
                    >
                      +
                    </button>
                    <span className="text-xs text-gray-500 w-6">{item.unit}</span>
                  </div>
                </div>
              ))}
            </div>
            {total > 0 && (
              <div className="text-right text-sm font-medium text-gray-700 mb-2">
                {t('agentChat.cards.total', 'Total')}: {total.toFixed(2)}₪
              </div>
            )}
            <button
              onClick={handleConfirm}
              className="w-full py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              {t('agentChat.cards.confirmSelection', 'Confirm Selection')} ({selectedItems.length})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
